/**
 * Quiz Results / Celebration Screen
 */

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import confetti from "canvas-confetti";

interface ResultsState {
  correctCount: number;
  total: number;
}

export default function QuizResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ResultsState | null;
  const fired = useRef(false);

  const correctCount = state?.correctCount ?? 0;
  const total = state?.total ?? 0;
  const percentage = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 6,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ["#a855f7", "#6366f1", "#22d3ee", "#f59e0b"],
      });
      confetti({
        particleCount: 6,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ["#a855f7", "#6366f1", "#22d3ee", "#f59e0b"],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  const emoji =
    percentage === 100 ? "🏆" : percentage >= 70 ? "🎉" : percentage >= 40 ? "💪" : "📚";

  const message =
    percentage === 100
      ? "Perfect score!"
      : percentage >= 70
      ? "Great work!"
      : percentage >= 40
      ? "Good effort!"
      : "Keep practicing!";

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-8">
        {/* Emoji */}
        <div className="text-7xl">{emoji}</div>

        {/* Score */}
        <div className="space-y-2">
          <h1 className="text-4xl font-extrabold text-foreground">
            {message}
          </h1>
          <p className="text-2xl font-bold text-primary">
            You got {correctCount}/{total} questions correct!
          </p>
          <p className="text-muted-foreground">
            That's {percentage}% — {percentage >= 70 ? "you're doing great!" : "keep practicing to improve!"}
          </p>
        </div>

        {/* Score ring */}
        <div className="flex justify-center">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18" cy="18" r="15.9"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="3"
              />
              <circle
                cx="18" cy="18" r="15.9"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="3"
                strokeDasharray={`${percentage} ${100 - percentage}`}
                strokeLinecap="round"
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-foreground">{percentage}%</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <Button
          size="lg"
          className="w-full text-lg py-6 h-auto font-bold"
          onClick={() => navigate("/meeting-simulator")}
        >
          Continue Learning
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
